// Suggested location: src/services/chat/firebaseChatService.js
//
// FirebaseChatService
//
// Manages the conversations list (ConversationsScreen).
// For per-conversation message streaming, use FirebaseMessageService.
//
// Firestore structure:
//   /chats/{conversationId}
//       participants        : string[]
//       participantsKey     : string
//       lastMessage         : string
//       lastMessageSender   : string
//       lastMessageTime     : Timestamp
//       lastMessageType     : string
//       lastMessageStatus   : string   ("sent"|"delivered"|"read")
//       unreadCount         : { [uid]: number }
//       isArchived          : boolean
//       isMuted             : boolean
//       isPinned            : boolean
//       mutedUntil          : Timestamp | null
//       lastSeen            : { [uid]: Timestamp }
//       createdAt           : Timestamp
//       deletedAt           : Timestamp | null
//       deletedBy           : string[]
//
//   /chats/{conversationId}/messages/{messageId}
//       (see FirebaseMessageService for full schema)

import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { chatLocalDb } from './chatLocalDb';

// ── Enums (mirrors chat_message.dart / chat_conversation.dart) ─────────────

export const MessageStatus = Object.freeze({
  sending: 'sending',
  sent: 'sent',
  delivered: 'delivered',
  read: 'read',
  failed: 'failed',
});

export const MessageType = Object.freeze({
  text: 'text',
  image: 'image',
  audio: 'audio',
  video: 'video',
  file: 'file',
  deleted: 'deleted',
});

// ── Tiny broadcast-stream helper (mirrors dart:async StreamController) ─────
//
// .subscribe(listener) returns an unsubscribe function, same contract as
// the onSnapshot listeners this service already hands out elsewhere.
class BroadcastStream {
  #listeners = new Set();
  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
  add(value) {
    for (const listener of this.#listeners) listener(value);
  }
  close() {
    this.#listeners.clear();
  }
}

class FirebaseChatServiceImpl {
  // ── State ──────────────────────────────────────────────────────────────

  _currentUserId = null;
  _initialized = false;

  /** @type {Map<string, () => void>} conversationId -> unsubscribe */
  _messageListeners = new Map();

  // ── Streams ────────────────────────────────────────────────────────────

  messageStream = new BroadcastStream();
  errorStream = new BroadcastStream();
  connectionStream = new BroadcastStream();
  conversationsStream = new BroadcastStream();

  // ── conversationsStream listener subscription ─────────────────────────

  _conversationsUnsub = null;

  // ── Helpers ────────────────────────────────────────────────────────────

  get currentUserId() {
    return this._currentUserId ?? '';
  }

  /** Deterministic conversation ID: sorted UIDs joined with "-" */
  static buildConversationId(uid1, uid2) {
    const parts = [uid1, uid2].sort();
    return `${parts[0]}-${parts[1]}`;
  }

  /** Deterministic participants key for Firestore queries */
  static buildParticipantsKey(uid1, uid2) {
    const parts = [uid1, uid2].sort();
    return `${parts[0]}_${parts[1]}`;
  }

  // ── Connect ────────────────────────────────────────────────────────────

  async connect() {
    if (this._initialized) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        this.errorStream.add('User not authenticated');
        this.connectionStream.add(false);
        return;
      }

      this._currentUserId = user.uid;
      this._initialized = true;
      this.connectionStream.add(true);

      this._listenToConversations();

      if (process.env.NODE_ENV !== 'production') {
        console.log(`FirebaseChatService connected as ${this._currentUserId}`);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('FirebaseChatService connect error:', e);
      }
      this.errorStream.add(`Connection failed: ${e}`);
      this.connectionStream.add(false);
    }
  }

  // ── Conversations listener ────────────────────────────────────────────

  _listenToConversations() {
    this._conversationsUnsub?.();

    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', this._currentUserId),
      orderBy('lastMessageTime', 'desc'),
    );

    this._conversationsUnsub = onSnapshot(
      q,
      (snapshot) => {
        const conversations = snapshot.docs
          .map((d) => this._conversationFromDoc(d))
          .filter((c) => c !== null);

        this.conversationsStream.add(conversations);
      },
      (e) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Conversations listener error:', e);
        }
        this.errorStream.add(`Conversations error: ${e}`);
      },
    );
  }

  _conversationFromDoc(docSnap) {
    try {
      const data = docSnap.data();
      if (!data) return null;

      const participants = Array.isArray(data.participants) ? data.participants : [];
      const otherId = participants.find((p) => p !== this._currentUserId) ?? '';

      if (!otherId) return null;

      const lastMessageTime =
        data.lastMessageTime instanceof Timestamp
          ? data.lastMessageTime.toDate()
          : new Date();

      const unreadMap = data.unreadCount ?? {};
      const unreadCount = Number(unreadMap[this._currentUserId] ?? 0);

      // Parse last message status
      let lastMsgStatus = MessageStatus.sent;
      const rawStatus = data.lastMessageStatus?.toString();
      if (rawStatus) {
        switch (rawStatus) {
          case 'delivered':
            lastMsgStatus = MessageStatus.delivered;
            break;
          case 'read':
            lastMsgStatus = MessageStatus.read;
            break;
          case 'sending':
            lastMsgStatus = MessageStatus.sending;
            break;
          case 'failed':
            lastMsgStatus = MessageStatus.failed;
            break;
          default:
            lastMsgStatus = MessageStatus.sent;
        }
      }

      return {
        id: docSnap.id,
        otherUserId: otherId,
        lastMessage: data.lastMessage?.toString() ?? '',
        lastMessageTime,
        lastMessageSenderId: data.lastMessageSender?.toString() ?? '',
        lastMessageStatus: lastMsgStatus,
        unreadCount,
        participants,
        lastMessageType: data.lastMessageType?.toString() ?? 'text',
        isArchived: data.isArchived === true,
        isMuted: data.isMuted === true,
        isPinned: data.isPinned === true,
      };
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Error parsing conversation doc:', e);
      }
      return null;
    }
  }

  // ── Messages stream (kept for legacy compat — prefer FirebaseMessageService) ─

  /**
   * Subscribes to a conversation's messages, ordered oldest → newest.
   * Returns an unsubscribe function (call it in a useEffect cleanup),
   * matching the Stream<List<ChatMessage>> contract from Dart.
   */
  getMessagesStream(conversationId, onMessages, onError) {
    const q = query(
      collection(db, 'chats', conversationId, 'messages'),
      orderBy('ts', 'asc'),
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((d) => this._messageFromDoc(d, conversationId))
          .filter((m) => m !== null);
        onMessages(messages);
      },
      onError,
    );
  }

  _messageFromDoc(docSnap, conversationId) {
    try {
      const data = docSnap.data();
      if (!data) return null;

      const ts = data.ts;
      let timestamp;
      if (ts instanceof Timestamp) {
        timestamp = ts.toDate();
      } else if (typeof ts === 'string') {
        const parsed = new Date(ts);
        timestamp = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
      } else {
        timestamp = new Date();
      }

      return {
        id: docSnap.id,
        conversationId,
        senderId: data.from?.toString() ?? '',
        receiverId: data.to?.toString() ?? '',
        content: data.msg?.toString() ?? '',
        timestamp,
        status: FirebaseChatServiceImpl._statusFromString(data.status?.toString()),
        messageType: FirebaseChatServiceImpl._typeFromString(data.msgType?.toString()),
        attachmentUrl: data.attachmentUrl?.toString(),
        attachmentType: data.attachmentType?.toString(),
        seenBy: Array.isArray(data.seenBy) ? data.seenBy : [],
        isDeleted: data.isDeleted === true,
      };
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Error parsing message doc:', e);
      }
      return null;
    }
  }

  // ── Send message ───────────────────────────────────────────────────────

  async sendMessage(message) {
    if (this._currentUserId == null) throw new Error('Not connected');

    const conversationId = message.conversationId;
    const convRef = doc(db, 'chats', conversationId);
    const msgRef = doc(db, 'chats', conversationId, 'messages', message.id);
    const now = serverTimestamp();
    const clientNow = new Date();

    await chatLocalDb.clearDeletedFlag(conversationId);

    const batch = writeBatch(db);

    batch.set(msgRef, {
      from: message.senderId,
      to: message.receiverId,
      msg: message.content,
      ts: Timestamp.fromDate(clientNow),
      msgType: message.messageType,
      status: 'sent',
      ...(message.attachmentUrl != null ? { attachmentUrl: message.attachmentUrl } : {}),
      ...(message.attachmentType != null ? { attachmentType: message.attachmentType } : {}),
      seenBy: [message.senderId],
      isDeleted: false,
    });

    batch.set(
      convRef,
      {
        participants: arrayUnion(message.senderId, message.receiverId),
        deletedBy: arrayRemove(message.senderId),
        lastMessage: FirebaseChatServiceImpl._lastMessagePreview(
          message.content,
          message.messageType,
        ),
        lastMessageSender: message.senderId,
        lastMessageTime: now,
        lastMessageType: message.messageType,
        lastMessageStatus: 'sent',
        [`unreadCount.${message.receiverId}`]: increment(1),
        [`unreadCount.${message.senderId}`]: 0,
      },
      { merge: true },
    );

    await batch.commit();
  }

  // ── Ensure conversation exists ────────────────────────────────────────

  async ensureConversation(otherUserId) {
    const myId = this._currentUserId;
    const convId = FirebaseChatServiceImpl.buildConversationId(myId, otherUserId);
    const convRef = doc(db, 'chats', convId);

    await chatLocalDb.clearDeletedFlag(convId);

    const snap = await getDoc(convRef);

    if (!snap.exists()) {
      const sortedParticipants = [myId, otherUserId].sort();
      await batchSetConversation(convRef, {
        participants: sortedParticipants,
        participantsKey: FirebaseChatServiceImpl.buildParticipantsKey(myId, otherUserId),
        lastMessage: '',
        lastMessageSender: '',
        lastMessageTime: serverTimestamp(),
        lastMessageType: 'text',
        lastMessageStatus: 'sent',
        unreadCount: { [myId]: 0, [otherUserId]: 0 },
        isArchived: false,
        isMuted: false,
        isPinned: false,
        mutedUntil: null,
        lastSeen: {},
        createdAt: serverTimestamp(),
        deletedAt: null,
        deletedBy: [],
        allParticipants: sortedParticipants,
      });
    } else {
      const data = snap.data() ?? {};
      const participants = Array.isArray(data.participants) ? data.participants : [];
      if (!participants.includes(myId)) {
        await batchUpdateConversation(convRef, {
          participants: arrayUnion(myId),
          deletedBy: arrayRemove(myId),
        });
      }
    }

    return convId;
  }

  // ── Mark conversation as read ─────────────────────────────────────────

  async markConversationAsRead(conversationId) {
    const myId = this._currentUserId;
    if (!myId) return;

    try {
      await batchSetConversation(
        doc(db, 'chats', conversationId),
        {
          [`unreadCount.${myId}`]: 0,
          [`lastSeen.${myId}`]: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('markAsRead error:', e);
      }
    }
  }

  // ── Fetch conversations (one-shot) ────────────────────────────────────

  async fetchConversations() {
    try {
      const q = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', this._currentUserId),
        orderBy('lastMessageTime', 'desc'),
      );
      const snap = await getDocs(q);

      return snap.docs
        .map((d) => this._conversationFromDoc(d))
        .filter((c) => c !== null);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('fetchConversations error:', e);
      }
      return [];
    }
  }

  // ── Disconnect / dispose ──────────────────────────────────────────────

  async disconnect() {
    this._conversationsUnsub?.();
    this._conversationsUnsub = null;
    for (const unsub of this._messageListeners.values()) unsub();
    this._messageListeners.clear();
    this._initialized = false;
    this._currentUserId = null;
    this.connectionStream.add(false);
  }

  dispose() {
    this.disconnect();
    this.messageStream.close();
    this.errorStream.close();
    this.connectionStream.close();
    this.conversationsStream.close();
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  static _statusFromString(v) {
    switch (v) {
      case 'sent':
        return MessageStatus.sent;
      case 'delivered':
        return MessageStatus.delivered;
      case 'read':
        return MessageStatus.read;
      case 'failed':
        return MessageStatus.failed;
      default:
        return MessageStatus.sending;
    }
  }

  static _typeFromString(v) {
    switch (v) {
      case 'image':
        return MessageType.image;
      case 'audio':
        return MessageType.audio;
      case 'video':
        return MessageType.video;
      case 'file':
        return MessageType.file;
      case 'deleted':
        return MessageType.deleted;
      default:
        return MessageType.text;
    }
  }

  static _lastMessagePreview(content, type) {
    switch (type) {
      case MessageType.image:
        return '📷 Photo';
      case MessageType.audio:
        return '🎤 Voice message';
      case MessageType.video:
        return '🎥 Video';
      case MessageType.file:
        return `📎 ${content ? content : 'File'}`;
      case MessageType.deleted:
        return 'This message was deleted';
      default:
        return content;
    }
  }
}

function batchSetConversation(ref, data, options) {
  return setDoc(ref, data, options);
}
function batchUpdateConversation(ref, data) {
  return updateDoc(ref, data);
}

// ── Singleton export (mirrors the Dart factory constructor) ────────────
export const firebaseChatService = new FirebaseChatServiceImpl();
export default firebaseChatService;